/**
 * GlassCard — Glassmorphism card wrapper with optional mouse glow effect
 */
import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Box } from '@mui/material';
import { colors } from '../../styles/colors';

function GlassCard({
  children,
  sx = {},
  hover = true,
  glow = true,
  dark = false,
  onClick,
  onMouseEnter: onMouseEnterProp,
  onMouseLeave: onMouseLeaveProp,
  delay = 0,
  ...props
}) {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!glow || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, [glow]);

  const glowGradient = isHovered && glow
    ? `radial-gradient(300px circle at ${mousePos.x}px ${mousePos.y}px, rgba(27,94,64,0.07), transparent 60%)`
    : 'none';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
    >
      <Box
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={(e) => { setIsHovered(true); onMouseEnterProp?.(e); }}
        onMouseLeave={(e) => { setIsHovered(false); onMouseLeaveProp?.(e); }}
        onClick={onClick}
        sx={{
          position: 'relative',
          borderRadius: '20px',
          border: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : colors.borderLight}`,
          background: dark ? 'rgba(255,255,255,0.06)' : colors.cardGlass,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          overflow: 'hidden',
          transition: 'box-shadow 0.3s ease, border-color 0.3s ease, transform 0.3s ease',
          cursor: onClick ? 'pointer' : 'default',
          ...(hover && {
            '&:hover': {
              boxShadow: dark
                ? '0 8px 32px rgba(27,94,64,0.15)'
                : '0 8px 32px rgba(27,94,64,0.08)',
              borderColor: dark ? 'rgba(255,255,255,0.12)' : colors.border,
              transform: 'translateY(-2px)',
            },
          }),
          ...sx,
        }}
        {...props}
      >
        {/* Mouse glow overlay */}
        {glow && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: glowGradient,
              pointerEvents: 'none',
              zIndex: 0,
              borderRadius: 'inherit',
            }}
          />
        )}
        {/* Content */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {children}
        </Box>
      </Box>
    </motion.div>
  );
}

export default GlassCard;
